import React, { useState } from "react";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";
import AdminBulkRefundModal from "../../components/AdminPages/AdminBulkRefundModal";
import InvoiceExportPanel from "../../components/AdminPages/InvoiceExportPanel/InvoiceExportPanel";

import { fetchTransactionsAdmin, fetchTransactionInvoices } from "../../services/TransactionService";

// Always visible transaction columns
const alwaysVisibleColumns = [
  { key: "name", label: "Name", width: "300px" },
  { key: "transaction_type", label: "Type", width: "140px" },
  { key: "user_id", label: "User ID", width: "300px" },
  { key: "amount", label: "Amount", width: "300px" },
  { key: "success", label: "Success", width: "140px" },
  { key: "user_country_id", label: "User Country ID", width: "160px" },
  { key: "wallet_id", label: "Wallet ID", width: "320px" },
  { key: "payment.provider", label: "Provider", width: "140px" },
  { key: "date", label: "Date", width: "320px" },
];

// Optional columns that can be toggled
const optionalColumns = [
  { key: "id", label: "Transaction ID", width: "320px" },
  { key: "payment.status", label: "Payment Status", width: "140px" },
  { key: "promocode_code", label: "Promocode", width: "140px" },
  { key: "external_transaction_id", label: "External ID", width: "500px" },
  { key: "payment.external_id", label: "Payment External ID", width: "500px" },
  { key: "related_transaction_id", label: "Related Transaction ID", width: "300px" },
  { key: "related_transaction.name", label: "Related Transaction Name", width: "240px" },
];

// Sort options
const sortOptions = [
  { value: "transaction.name", label: "Name" },
  { value: "transaction.date", label: "Date" },
  { value: "transaction.amount", label: "Amount" },
  { value: "transaction.transaction_type", label: "Type" },
  { value: "transaction.user_id", label: "User ID" },
  { value: "transaction.user_country_id", label: "User Country ID" },
  { value: "transaction.wallet_id", label: "Wallet ID" },
  { value: "transaction.id", label: "Transaction ID" },
  { value: "transaction.success", label: "Success" },
  { value: "payment.provider", label: "Provider" },
  { value: "payment.status", label: "Payment Status" },
  { value: "transaction.promocode_code", label: "Promocode" },
];

// Filters
const filterDefinitions = [
  { key: "name", label: "Name", type: "text" },
  { key: "promocode_code", filterKey: "promocode", label: "Promocode", type: "text" },
  { key: "user_id", label: "User ID", type: "text" },
  { key: "user_country_id", label: "User Country ID", type: "text" },
  { key: "payment.provider", filterKey: "provider", label: "Provider", type: "text" },
  { key: "payment.external_id", filterKey: "external_transaction_id", label: "External ID", type: "text" },
  { key: "related_transaction_id", label: "Related Transaction Id", type: "text" },
  { key: "external_transaction_id", label: "External Transaction Id", type: "text" },
  {
    key: "related_transaction.name",
    filterKey: "related_transaction_name",
    label: "Related Transaction Name",
    type: "text",
  },

  { key: "wallet_id", label: "Wallet ID", type: "text" },
  { key: "amount", label: "Amount", type: "minmax", inputType: "number" },
  { key: "currency", label: "Currency", type: "text" },
  {
    key: "id",
    filterKey: "transaction_id",
    label: "Transaction ID",
    type: "text",
  },
  {
    key: "payment.status",
    filterKey: "payment_status",
    label: "Payment Status",
    type: "select",
    options: [
      { value: "pending", label: "Pending" },
      { value: "succeeded", label: "Succeeded" },
      { value: "failed", label: "Failed" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  {
    key: "transaction_type",
    filterKey: "type",
    label: "Type",
    type: "select",
    options: [
      { value: "topup", label: "Top-Up" },
      { value: "service", label: "Service" },
      { value: "api", label: "API" },
      { value: "transfer", label: "Transfer" },
      { value: "invoice", label: "Invoice" },
    ],
  },
  {
    key: "success",
    label: "Success",
    type: "select",
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ],
  },
  {
    key: "date",
    label: "Date",
    type: "date-range",
    minKey: "date_from",
    maxKey: "date_to",
  },
];

// Custom cell renderers
const customRenderers = {
  success: (item) => (
    <span
      className={item.success === true ? "badge-active" : item.success === false ? "badge-inactive" : "badge-neutral"}
    >
      {item.success === true ? "Yes" : item.success === false ? "No" : "N/A"}
    </span>
  ),
  date: (item) => {
    if (!item.date) return "-";
    return new Date(item.date).toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  },
  amount: (item) => `${item.amount?.toFixed(2) || 0} ${item.currency || ""}`,
};

function getFilenameFromDisposition(contentDisposition, fallback = "transaction-invoices.zip") {
  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename\s*=\s*"?(.*?)"?(\s*;|$)/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }

  return fallback;
}

export default function AdminTransactions() {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkRefundOpen, setIsBulkRefundOpen] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  const handleBulkAction = (ids, rows) => {
    setSelectedIds(ids);
    setSelectedTransactions(rows);
    setIsBulkRefundOpen(true);
  };

  const handleRefundComplete = (successes, failures) => {
    setIsBulkRefundOpen(false);
    // Refresh tabeli
    window.location.reload();
  };

  const handleFetchInvoices = async (params) => {
    const { blob, contentDisposition, contentType } = await fetchTransactionInvoices(params);

    const filename = getFilenameFromDisposition(contentDisposition, "transaction-invoices.zip");
    const downloadBlob = new Blob([blob], { type: contentType || "application/zip" });
    const url = window.URL.createObjectURL(downloadBlob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  };

  return (
    <main className="admin-transactions-page">
      <PageHeader title="Transactions" description="View and manage all user transactions" />

      <DataTable
        tableId="admin-transactions"
        fetchDataFunction={fetchTransactionsAdmin}
        columns={alwaysVisibleColumns}
        optionalColumns={optionalColumns}
        sortOptions={sortOptions}
        filterDefinitions={filterDefinitions}
        defaultSortBy="transaction.date"
        itemKey="id"
        customRenderers={customRenderers}
        showCheckboxes={true}
        bulkActionLabel="Bulk Action"
        onBulkAction={handleBulkAction}
      />

      <InvoiceExportPanel onFetch={handleFetchInvoices} />

      <AdminBulkRefundModal
        isOpen={isBulkRefundOpen}
        selectedIds={selectedIds}
        selectedTransactions={selectedTransactions}
        onClose={() => setIsBulkRefundOpen(false)}
        onComplete={handleRefundComplete}
      />
    </main>
  );
}
